import { defineStore } from 'pinia'
import { advertiserMapping } from '@/constants/selectOptions'

export const useLeadStore = defineStore('lead', {
  state: () => ({
    // Advertiser/buyer assigned to this lead
    advertiser: '0',

    // Submission state
    isSubmitting: false,
    isSubmitted: false,
    submissionResult: null,
    submissionError: null,

    // Lead ID from Leadspedia
    leadId: null,

    // Revenue from lead
    revenue: 0,

    // Lead post attempt counter (for logging)
    leadPostAttemptCounter: 0,

    // CRS (Credit Reporting Service) data
    crsData: null,        // Raw CRS response from Lambda
    verifiedDebt: null,   // Server-calculated verified debt
    crsError: null,       // Error info if CRS failed
    forceCRS: false,      // True when ?crs=1 URL param is present (test mode)
    crsFallback: null,    // "Yes" when CRS was used as fallback after Array no-hit
    creditPullStrategy: null // Lambda strategy metadata for explicit Array/CRS routing
  }),

  getters: {
    // Get advertiser details from mapping
    advertiserDetails: (state) => {
      return advertiserMapping[state.advertiser] || null
    },

    // Check if we have a successful advertiser match
    hasAdvertiserMatch: (state) => {
      return state.advertiser !== '0' && advertiserMapping[state.advertiser]
    },

    // Get redirect URL with lead info
    redirectUrl: (state) => {
      return (lpLeadId, affId, acContactId) => {
        const ac = acContactId ? 'Yes' : 'No'
        return `https://firstadvantage.io/?lid=${lpLeadId}&aid=${affId}&ac=${ac}`
      }
    }
  },

  actions: {
    // Set advertiser from lead response clients array
    setAdvertiserFromClients(clients) {
      if (!clients || !Array.isArray(clients)) return

      for (let i = 0; i < clients.length; i++) {
        if (clients[i]?.client?.success === 'true') {
          this.advertiser = String(clients[i].client.advertiserID)
          break
        }
      }
    },

    // Set advertiser directly
    setAdvertiser(advertiserId) {
      this.advertiser = String(advertiserId)
    },

    // Set revenue
    setRevenue(revenue) {
      this.revenue = Number(revenue) || 0
    },

    // Set lead ID
    setLeadId(leadId) {
      this.leadId = leadId
    },

    // Set submitted flag
    setSubmitted(submitted) {
      this.isSubmitted = submitted
    },

    // Increment lead post attempt counter
    incrementAttemptCounter() {
      this.leadPostAttemptCounter++
      return this.leadPostAttemptCounter
    },

    // Set submission started
    startSubmission() {
      this.isSubmitting = true
      this.submissionError = null
    },

    // Set submission completed
    completeSubmission(result) {
      this.isSubmitting = false
      this.submissionResult = result
    },

    // Set submission error
    setSubmissionError(error) {
      this.isSubmitting = false
      this.submissionError = error
    },

    // Set CRS data from Lambda response
    setCrsData(crsData) {
      this.crsData = crsData
    },

    // Set verified debt from Lambda response
    setVerifiedDebt(verifiedDebt) {
      this.verifiedDebt = verifiedDebt
    },

    // Set CRS error if CRS call failed
    setCrsError(crsError) {
      this.crsError = crsError
    },

    // Set forceCRS flag (test mode)
    setForceCRS(forceCRS) {
      this.forceCRS = forceCRS
    },

    // Set crsFallback flag (when CRS used as fallback after Array no-hit)
    setCrsFallback(crsFallback) {
      this.crsFallback = crsFallback
    },

    // Set credit pull strategy metadata from Lambda response
    setCreditPullStrategy(creditPullStrategy) {
      this.creditPullStrategy = creditPullStrategy
    },

    // Reset submission state
    resetSubmission() {
      this.isSubmitting = false
      this.submissionResult = null
      this.submissionError = null
    },

    // Check if lead should show thank you page
    shouldShowThankYou(verifiedDebt = 0) {
      const adv = this.advertiser

      // Specific advertiser rules
      if (adv === '2') return true
      if (adv === '24' && Number(verifiedDebt) > 1) return true
      if (adv === '30') return true

      // General rule: advertiser match with positive revenue
      if (this.hasAdvertiserMatch && this.revenue > 1) return true

      return false
    },

    // Get custom redirect URL based on lead data
    getCustomRedirectUrl(data, called = false) {
      let turboLink = 'https://www.ab92trk.com/2X2ZMNN/45TJ3T/?sub1=fa1&sub2='
      if (called) {
        turboLink = 'https://www.ab92trk.com/2X2ZMNN/6FQ5XR/?sub1=fa2&sub2='
      }

      const verifiedDebt = data.verified_debt || 0
      const revenue = this.revenue

      let redirectUrl = `https://firstadvantage.io/programs/save.html?vd=${verifiedDebt}`

      // Low/no debt scenarios redirect to turbo link
      if (verifiedDebt === 9949) {
        redirectUrl = this.buildTurboLink(turboLink, data)
      } else if (verifiedDebt === 0 && revenue < 1) {
        redirectUrl = this.buildTurboLink(turboLink, data)
      } else if (verifiedDebt > 10000 && revenue < 1) {
        redirectUrl = this.buildTurboLink(turboLink, data)
      }

      return redirectUrl
    },

    buildTurboLink(baseUrl, data) {
      return baseUrl +
        (data.aff_id || '') +
        '&first=' + encodeURIComponent(data.first_name || '') +
        '&last=' + encodeURIComponent(data.last_name || '') +
        '&phone=' + encodeURIComponent(data.phone_home || '') +
        '&email=' + encodeURIComponent(data.email_address || '') +
        '&state=' + encodeURIComponent(data.state || '')
    }
  }
})
